import { Box } from "@mui/material";
import { useTheme } from "@mui/material";

import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Text from "../../../../theme/text/Text";

import { useEffect, useState, useMemo } from "react";
import Subheading from "../../../../theme/text/Subheading";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";

import { useTranslation } from "react-i18next";
import * as yup from "yup";
import ShValidatedInput, { ShValidatedArea } from "../../../../theme/inputs/ShValidatedInput";
import { yupRequired } from "../../../../../utils/validation";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import { useUpdateOrderMutation, useGetServicePointsQuery } from "../../../../../newapi/order/ordersSlice";
import { showToastMessageRequestError, showToastMessageUpdateRequest, showToastMessage} from "../../../../../utils/toasts";
import SaveIcon from '@mui/icons-material/Save';
import { countriesFromLocale } from '../../../../../utils/countries';
import ShValidatedInputDropdown from "../../../../theme/dropdowns/ShValidatedInputDropdown";
import { useSelector } from 'react-redux';
import { UserPrivileges } from "../../../../../utils/permissions";
import { selectUserPermission } from '../../../../../newapi/user/authentication/authSlice';
import { general_states } from "../../../../../utils/staticEnums";
import EMGeneralExplainerBox from "../../../ModalsPopover/explainers/EMGeneralExplainerBox";
import { countryCodeNL } from "../../../../../utils/constants";
import { useGetShippersSelectQuery } from "../../../../../newapi/shipper/shipperSlice";
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return props.hasPermission && props.canEdit ?
        <ButtonGroup version={2}>
            <Shbutton
                className={"group-btn"}
                onClick={props.onclick}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                isLoading={props.isLoading}
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
        </ButtonGroup> : <></>;
};

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    hide:true,
    

};

const ShipOrder = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 
    const [data, setData] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [canEdit, setCanEdit] = useState(false);
    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);
    const hasOrderManagePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MANAGE, UserPrivileges.RP_ORDER_MANAGE])
    );

    // Fetch shippers with service points support
    const { data: shippersData } = useGetShippersSelectQuery({ withServicePoints: true });

    // State for shipper and service point selection
    const [selectedShipperIndex, setSelectedShipperIndex] = useState(-1);
    const [selectedServicePointIndex, setSelectedServicePointIndex] = useState(-1);

    // Prepare shipper options with "home_delivery" as first option
    const shipperOptions = useMemo(() => {
        if (!shippersData) return [];
        return [
            { id: 'home_delivery', name: t('home_delivery'), type: 'home_delivery' },
            ...shippersData
        ];
    }, [shippersData, t]);

    const [orderData, setOrderData] = useState({
        shippingAddress: {
            street: "",
            houseNumber: "",
            houseNumberAddition: "",
            addressLine1: "",
            addressLine2: "",
            postalCode: "",
            city: "",
            country: "",
            state: "",
            phone: "",
            email: "",
            company: "",
            firstName: "",
            lastName: "",
            insertion: "",
            name: "",
            skipValidation: false,
        },
        remark: ""
    });

    useEffect(() => {
        if(props.data){
            setOrderData(prevState => ({
                ...prevState,
                shippingAddress: {
                    street: props.data.shippingAddress?.street,
                    houseNumber: props.data.shippingAddress?.houseNumber,
                    houseNumberAddition: props.data.shippingAddress?.houseNumberAddition,
                    addressLine1: props.data.shippingAddress?.addressLine1,
                    addressLine2: props.data.shippingAddress?.addressLine2,
                    postalCode: props.data.shippingAddress?.postalCode,
                    state: props.data.shippingAddress?.state,
                    city: props.data.shippingAddress?.city,
                    email: props.data.shippingAddress?.email,
                    phone: props.data.shippingAddress?.phone,
                    country: props.data.shippingAddress?.country,
                    company: props.data.shippingAddress?.company,
                    firstName: props.data.shippingAddress?.firstName,
                    lastName: props.data.shippingAddress?.lastName,
                    insertion: props.data.shippingAddress?.insertion,
                    name: props.data.shippingAddress?.name,
                    pickupCarrier: props.data.shippingAddress?.pickupCarrier,
                    pickupPointData: props.data.shippingAddress?.pickupPointData,
                    pickupPoint: props.data.shippingAddress?.pickupPoint,
                    pickupPointName: props.data.shippingAddress?.pickupPointName,
                    skipValidation: props.data.shippingAddress?.skipValidation ?? false,
                },
                remark: props.data.remark,
                // ...props.data,
            }));
        
            setCanEdit(![general_states.SHIPPED].includes(props.data.state));

            // Restore shipper selection
            if (props.data.shippingAddress?.pickupCarrier && shipperOptions.length > 0) {
                const index = shipperOptions.findIndex(s => s.type === props.data.shippingAddress.pickupCarrier);
                if (index !== -1) {
                    setSelectedShipperIndex(index);
                }
            } else if (props.data.shippingAddress?.pickupPoint === null && shipperOptions.length > 0) {
                // If no pickup point and no carrier, default to home_delivery
                setSelectedShipperIndex(0);
            }
        }
    }, [props.data, shipperOptions]);

    // Get selected shipper
    const selectedShipper = useMemo(() => {
        if (selectedShipperIndex === -1) return null;
        return shipperOptions[selectedShipperIndex];
    }, [selectedShipperIndex, shipperOptions]);

    // Check if service point dropdowns should be shown
    const showShipperDropdown = useMemo(() => {
        // Show if country is NL OR if there's already a pickup point
        return orderData.shippingAddress?.country === countryCodeNL || orderData.shippingAddress?.pickupPoint;
    }, [orderData.shippingAddress?.country, orderData.shippingAddress?.pickupPoint]);

    // Check if service point dropdown should be shown
    const showServicePointDropdown = useMemo(() => {
        if (!selectedShipper || selectedShipper.id === 'home_delivery') return false;
        if (!selectedShipper.type) return false;
        return true;
    }, [selectedShipper]);

    // Check if structured address fields should be disabled
    const shouldDisableStructuredFields = useMemo(() => {
        return !["nl","be","de"].includes(orderData.shippingAddress?.country?.toLowerCase());
    }, [orderData.shippingAddress?.country]);

    // Fetch service points when conditions are met
    const shouldFetchServicePoints = showServicePointDropdown && orderData.shippingAddress?.postalCode &&
        orderData.shippingAddress?.city && selectedShipper?.type;
    const { data: servicePointsData } = useGetServicePointsQuery(
        {
            postal: orderData.shippingAddress?.postalCode,
            city: orderData.shippingAddress?.city,
            type: selectedShipper?.type
        },
        { skip: !shouldFetchServicePoints }
    );

    // Transform service point data based on shipper type
    const transformedServicePoints = useMemo(() => {
        if (!servicePointsData) return [];

        switch (selectedShipper?.type) {
            case 'dhl':
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    displayName: `${sp.name} - ${sp.street || ''} ${sp.houseNumber || ''} - ${sp.postalCode || ''} ${sp.city || ''} - ${sp.distance}km`,
                    rawData: sp
                }));
            case 'ampere':
            case 'zineps':
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    displayName: `${sp.name} - ${sp.street || ''} ${sp.houseNumber || ''} - ${sp.postalCode || ''} ${sp.city || ''} - ${sp.distance}km`,
                    rawData: sp
                }));
            case 'postnl':
                // PostNL returns a pre-combined address string and no distance value.
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    displayName: `${sp.name} - ${sp.address || ''} - ${sp.postalCode || ''} ${sp.city || ''}`,
                    rawData: sp
                }));
            case 'innosendv2':
                // Innosend brokers several couriers, so the points are grouped under a heading per
                // courier - carrierGroup is the short key ('dhl', 'postnl') rather than the full courier
                // name, which reads as "DHLEuroPlus Innosend".
                // Its address is pre-combined like PostNL's and it returns no distance value.
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    carrierGroup: sp.carrierGroup ? sp.carrierGroup.toUpperCase() : sp.carrierGroup,
                    displayName: `${sp.name} - ${sp.address || ''} - ${sp.postalCode || ''} ${sp.city || ''}`,
                    rawData: sp
                }));
            default:
                return [];
        }
    }, [servicePointsData, selectedShipper?.type]);

    // Restore selected service point index from persisted data after fetching
    useEffect(() => {
        if (orderData.shippingAddress?.pickupPointData && transformedServicePoints.length > 0) {
            // Try to find the pickup point in the fetched service points
            const index = transformedServicePoints.findIndex(sp => {
                // Match by ID
                return sp.rawData.id === orderData.shippingAddress.pickupPointData.id;
            });

            if (index !== -1) {
                setSelectedServicePointIndex(index);
            } else {
                // If the saved pickup point is not in the fetched list, reset selection
                console.warn('Saved pickup point not found in fetched service points');
            }
        } else if (!orderData.shippingAddress?.pickupPoint && selectedServicePointIndex !== -1) {
            // Clear selection if pickup point was removed
            setSelectedServicePointIndex(-1);
        }
    }, [transformedServicePoints, orderData.shippingAddress?.pickupPoint]);

    const handleChange = (key, value) => {
        if (key === 'remark') {
            setOrderData(prevState => ({
                ...prevState,
                remark: value
            }));
        } else {
            setOrderData(prevState => ({
                ...prevState,
                shippingAddress: {
                    ...prevState.shippingAddress,
                    [key]: value
                }
            }));
        }
        // Optionally clear the error for that field
        setFieldErrors(prevState => ({
          ...prevState,
          [key]: ''
        }));
    };

    // Handle shipper selection
    const handleShipperChange = (key, val) => {
        // val is the ID from changeField="id"
        const shipper = shipperOptions.find(s => s.id === val);
        if (shipper) {
            const shipperIndex = shipperOptions.findIndex(s => s.id === val);
            setSelectedShipperIndex(shipperIndex);

            // Reset service point selection when shipper changes
            setSelectedServicePointIndex(-1);

            // Update shipping address with new shipper info
            setOrderData(prevState => ({
                ...prevState,
                shippingAddress: {
                    ...prevState.shippingAddress,
                    pickupCarrier: shipper.id === 'home_delivery' ? null : shipper.type,
                    pickupPoint: shipper.id === 'home_delivery' ? false : true,
                    pickupPointData: null,
                    pickupPointName: null
                }
            }));
        }
    };

    // Handle service point selection
    const handleServicePointChange = (key, val) => {
        if (val) {
            const arrIndex = transformedServicePoints.findIndex(x => x.id === val);
            if (arrIndex < 0) return;

            setSelectedServicePointIndex(arrIndex);
            const servicePoint = transformedServicePoints[arrIndex];

            setOrderData(prevState => ({
                ...prevState,
                shippingAddress: {
                    ...prevState.shippingAddress,
                    pickupPointData: servicePoint?.rawData,
                    pickupPointName: servicePoint?.rawData.name
                }
            }));
        }
    };
    
    const [EditOrder,{isLoading}] = useUpdateOrderMutation();
    
    
    const handleSaveClick = async () => {
        try {
            if (isLoading) return;
            
            const updateObject = {
                shippingAddress: orderData.shippingAddress,
                remark: orderData.remark,
                date_planned: orderData.shippingAddress?.date_planned,
                deliveryName: orderData.shippingAddress?.firstName?.trim() + (orderData.shippingAddress?.insertion?.trim().length > 0 ? " " + orderData?.shippingAddress?.insertion.trim() + " " : " ") + orderData?.shippingAddress?.lastName.trim(),
                id: props.data.id
            };
            
            const response = await EditOrder(updateObject).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            console.error(err);
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});
          
                // Set the validation errors in the state
                setFieldErrors(errors);
              } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("update_failed"), t, err);
              }
        }
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions:<GridActions isLoading={isLoading} canEdit={canEdit} hasPermission={hasOrderManagePermission} onclick={handleSaveClick}></GridActions>,
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[props.config,hasOrderManagePermission, canEdit, isLoading, handleSaveClick, props]);

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={1.5} paddingY={1.5} pb={10} overflow={"auto"}>
            {/* Details */}
            <Box 
                sx={{
                    display:"flex",
                    borderRadius:3,
                    overflow:"hidden",
                    marginBottom:1,
                    "@media screen and (max-width: 47.9375em)" : {
                        marginTop:0,
                        background:"none",
                        flexDirection:"column",
                        overflow:"unset"
                    }
                }}
            >
                {/* Editable content */}
                <Box padding={1.5} paddingRight={4} paddingBottom={3} flex={1}
                    sx={{
                        "@media screen and (max-width: 47.9375em)" : {
                            paddingY:0,
                            paddingRight:1.5,
                        }
                    }}
                >
                    <Box 
                        sx={{
                            flex:1,
                            display:"flex",
                            // alignItems:"center",
                            flexDirection:"column",
                            "@media screen and (max-width: 47.9375em)" : {
                                gap:0.5,
                                alignItems:"flex-start",
                                mt:2,
                            }
                        }}
                    >
                        <Subheading>{t("personal_details")}</Subheading>
                        {/* <Text>{props.data?.deliveryName}</Text> */}
                    </Box>

                    <Box
                        sx={{
                            mt:1.5,
                            width:1,
                            display:"flex",
                            gap:1,
                            alignItems:'center',
                            "@media screen and (max-width: 47.9375em)" : {
                                flexDirection:"row",
                                gap:5,
                                alignItems:"center",
                                width:1
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text light>{t("fullname")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1
                                }
                            }}
                        >
                            <Box display="flex"
                                borderRadius="20px"
                                sx={{
                                    marginTop:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                    }
                                }}
                            >
                                <ShValidatedInput
                                    readOnly={!hasOrderManagePermission}
                                    name="name"
                                    value={orderData.shippingAddress?.name ?? ""}
                                    onChange={handleChange}
                                    error={fieldErrors.shippingAddress?.name}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display:"flex",
                            mt:1.5,
                            "@media screen and (max-width: 47.9375em)" : {
                                flexDirection:"column"
                            }
                        }}
                    >
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                flexDirection:'column',
                                gap:1.5,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:1,
                                    alignItems:"flex-start",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    gap:1,
                                    alignItems:'center',
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        display:"flex",
                                        alignItems:"center",
                                    }}
                                >
                                    <Text light>{t("firstname")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            "@media screen and (max-width: 47.9375em)" : {
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            readOnly={!hasOrderManagePermission}
                                            name="firstName"
                                            value={orderData.shippingAddress?.firstName ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.firstName}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    gap:1,
                                    alignItems:'center',
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        display:"flex",
                                        alignItems:"center",
                                    }}
                                >
                                    <Text light>{t("insertion")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            "@media screen and (max-width: 47.9375em)" : {
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            readOnly={!hasOrderManagePermission}
                                            name="insertion"
                                            value={orderData.shippingAddress?.insertion ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.insertion}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    gap:1,
                                    alignItems:'center',
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        display:"flex",
                                        alignItems:"center",
                                    }}
                                >
                                    <Text light>{t("lastName")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            "@media screen and (max-width: 47.9375em)" : {
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            readOnly={!hasOrderManagePermission}
                                            name="lastName"
                                            value={orderData.shippingAddress?.lastName ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.lastName}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                        </Box>

                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                flexDirection:'column',
                                gap:1.5,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:1,
                                    alignItems:"flex-start",
                                }
                            }}
                        >
                            
                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1,
                                        mt:1.5,
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("phone")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                        }}
                                    >
                                    <ShValidatedInput
                                        sx={{
                                            px: 2, 
                                            flex:1, 
                                            height:32,
                                            boxSizing:"border-box",
                                        }}
                                        disabled={!hasOrderManagePermission}
                                        name="phone"
                                        value={orderData.shippingAddress?.phone ?? ""}
                                        onChange={handleChange}
                                        error={fieldErrors.shippingAddress?.phone}
                                    />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("email")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                    
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            sx={{
                                                px: 2, 
                                                flex:1, 
                                                height:32,
                                                boxSizing:"border-box"
                                            }}
                                            disabled={!hasOrderManagePermission}
                                            name="email"
                                            value={orderData.shippingAddress?.email ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.email}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("company")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                    
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            sx={{
                                                px: 2, 
                                                flex:1, 
                                                height:32,
                                                boxSizing:"border-box"
                                            }}
                                            disabled={!hasOrderManagePermission}
                                            name="company"
                                            value={orderData.shippingAddress?.company ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.company}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>


                    <Box 
                        sx={{
                            mt:5,
                            flex:1,
                            display:"flex",
                            // alignItems:"center",
                            flexDirection:"column",
                            "@media screen and (max-width: 47.9375em)" : {
                                gap:0.5,
                                alignItems:"flex-start",
                            }
                        }}
                    >
                        <Subheading>{t("shipment_details")}</Subheading>
                    </Box>

                    <Box
                        sx={{
                            display:"flex",
                            mt:1.5,
                            "@media screen and (max-width: 47.9375em)" : {
                                flexDirection:"column"
                            }
                        }}
                    >
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                flexDirection:'column',
                                gap:1.5,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:1,
                                    alignItems:"flex-start",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    gap:1,
                                    alignItems:'center',
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        display:"flex",
                                        alignItems:"center",
                                    }}
                                >
                                    <Text light>{t("address1")}</Text>
                                    <EMGeneralExplainerBox
                                        title={t("em_title_address1")}
                                        text={t("em_text_address1")}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            "@media screen and (max-width: 47.9375em)" : {
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            readOnly={!hasOrderManagePermission}
                                            name="addressLine1"
                                            value={orderData.shippingAddress?.addressLine1 ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.addressLine1}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display:"flex",
                                    gap:1,
                                    alignItems:'center',
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        
                                    }}
                                >
                                    <Text light>{t("address2")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                    <ShValidatedInput
                                        readOnly={!hasOrderManagePermission}
                                        name="addressLine2"
                                        value={orderData.shippingAddress?.addressLine2 ?? ""}
                                        onChange={handleChange}
                                        error={fieldErrors.shippingAddress?.addressLine2}
                                    />
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display:"flex",
                                    gap:1,
                                    alignItems:'center',
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        display:"flex",
                                        alignItems:"center",
                                    }}
                                >
                                    <Text light>{t("street")}</Text>
                                    <EMGeneralExplainerBox
                                        title={t("em_title_street")}
                                        text={t("em_text_street")}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        title={shouldDisableStructuredFields ? t("addressline_field_message") : ""}
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            position: 'relative',
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            disabled={shouldDisableStructuredFields || !hasOrderManagePermission}
                                            name="street"
                                            value={orderData.shippingAddress?.street ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.street}
                                        />
                                        {shouldDisableStructuredFields && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                title={t("addressline_field_message")}
                                            >
                                                <InfoOutlinedIcon sx={{ color: colors.grey[500], fontSize: 18 }} />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:'center',
                                    // flexDirection:"column",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 140,
                                        // "@media screen and (max-width: 47.9375em)" : {
                                        //     width:80
                                        // }
                                    }}
                                >
                                    <Text light>{t("house_nr")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        width:200,
                                        // "@media screen and (max-width: 47.9375em)" : {
                                        //     flex:1
                                        // }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        backgroundColor={colors.grey[200]}
                                        title={shouldDisableStructuredFields ? t("addressline_field_message") : ""}
                                        sx={{
                                            marginTop:0.5,
                                            background: (shouldDisableStructuredFields || !hasOrderManagePermission)
                                                    ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200])
                                                    : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]),
                                            position: 'relative',
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background: `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            sx={{
                                                px: 2,
                                                width:56,
                                                height:32,
                                                boxSizing:"border-box",
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flex:1,
                                                    width:"unset"
                                                }
                                            }}
                                            disabled={shouldDisableStructuredFields || !hasOrderManagePermission}
                                            name="houseNumber"
                                            value={orderData.shippingAddress?.houseNumber ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.houseNumber}
                                        />
                                        <Box
                                            sx={{
                                                marginY:0.5,
                                                height:24,
                                                width:"1px",
                                                background: (shouldDisableStructuredFields || !hasOrderManagePermission)
                                                    ? (theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[400])
                                                    : (theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[300])
                                            }}
                                        />
                                        <ShValidatedInput
                                            disabled={shouldDisableStructuredFields || !hasOrderManagePermission}
                                            sx={{px: 2, width:56, height:32,boxSizing:"border-box"}}
                                            name="houseNumberAddition"
                                            value={orderData.shippingAddress?.houseNumberAddition ?? ""}
                                            onChange={handleChange}
                                        />
                                        {shouldDisableStructuredFields && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                title={t("addressline_field_message")}
                                            >
                                                <InfoOutlinedIcon sx={{ color: colors.grey[500], fontSize: 18 }} />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:'center',
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                            width:140,
                                    }}
                                >
                                    <Text light>{t("postal_code")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <ShValidatedInput
                                            sx={{
                                                px: 2, 
                                                width:96, 
                                                height:32,
                                                boxSizing:"border-box",
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flex:1,
                                                    width:"unset"
                                                }
                                            }}
                                            disabled={!hasOrderManagePermission}
                                            name="postalCode"
                                            value={orderData.shippingAddress?.postalCode ?? ""}
                                            onChange={handleChange}
                                            error={fieldErrors.shippingAddress?.postalCode}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("city")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                    <ShValidatedInput
                                        sx={{
                                            px: 2, 
                                            flex:1, 
                                            height:32,
                                            boxSizing:"border-box",
                                            "@media screen and (max-width: 47.9375em)" : {
                                                flex:1,
                                                width:"unset"
                                            }
                                        }}
                                        disabled={!hasOrderManagePermission}
                                        name="city"
                                        value={orderData.shippingAddress?.city ?? ""}
                                        onChange={handleChange}
                                        error={fieldErrors.shippingAddress?.city}
                                    />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("country")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                        }}
                                    >
                                    <ShValidatedInputDropdown

                                            searchOptions={{
                                                enabled:true,
                                                placeholder:t("search")
                                            }}
                                            height={32}
                                            disabled={!hasOrderManagePermission}
                                            name="country"
                                            displayName="name"
                                            changeField="alpha2"
                                            options={countries ?? null}
                                            noSelection={t("choose_option")} 
                                            onChange={handleChange}
                                            selected={countries?.findIndex(country => (orderData.shippingAddress?.country && orderData.shippingAddress?.country.toLowerCase() === country.alpha2.toLowerCase())) ?? -1}
                                            error={fieldErrors.shippingAddress?.country}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("delivery_state")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        backgroundColor={colors.grey[200]}
                                        sx={{
                                        }}
                                    >
                                    <ShValidatedInput
                                        sx={{
                                            px: 2, 
                                            flex:1, 
                                            height:32,
                                            boxSizing:"border-box",
                                        }}
                                        disabled={!hasOrderManagePermission}
                                        name="state"
                                        value={orderData.shippingAddress?.state ?? ""}
                                        onChange={handleChange}
                                    />
                                    </Box>
                                </Box>
                            </Box>

                            {showShipperDropdown && (
                                <Box
                                    sx={{
                                        display:"flex",
                                        alignItems:"center",
                                        gap:1,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flexDirection:"row",
                                            gap:5,
                                            alignItems:"center",
                                            width:1
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:140
                                        }}
                                    >
                                        <Text light>{t("delivery_option")}</Text>
                                    </Box>

                                    <Box
                                        sx={{
                                            "@media screen and (max-width: 47.9375em)" : {
                                                flex:1
                                            }
                                        }}
                                    >
                                        <Box display="flex"
                                            borderRadius="20px"
                                            backgroundColor={colors.grey[200]}
                                            sx={{
                                            }}
                                        >
                                            <ShValidatedInputDropdown
                                                name="shipper"
                                                displayName="name"
                                                changeField="id"
                                                options={shipperOptions}
                                                noSelection={t("choose_option")}
                                                onChange={handleShipperChange}
                                                selected={selectedShipperIndex}
                                                closeOnSelection
                                                disabled={!hasOrderManagePermission || !canEdit}
                                                styles={{
                                                    minWidth:300
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {showServicePointDropdown && transformedServicePoints.length > 0 && (
                                <Box
                                    sx={{
                                        display:"flex",
                                        alignItems:"center",
                                        gap:1,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flexDirection:"row",
                                            gap:5,
                                            alignItems:"center",
                                            width:1
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:140
                                        }}
                                    >
                                        <Text light>{t("service_point")}</Text>
                                    </Box>

                                    <Box
                                        sx={{
                                            "@media screen and (max-width: 47.9375em)" : {
                                                flex:1
                                            }
                                        }}
                                    >
                                        <Box display="flex"
                                            borderRadius="20px"
                                            backgroundColor={colors.grey[200]}
                                            sx={{
                                            }}
                                        >
                                            <ShValidatedInputDropdown
                                                name="servicePoint"
                                                displayName="displayName"
                                                optionGroupKey="carrierGroup"
                                                changeField="id"
                                                options={transformedServicePoints}
                                                noSelection={t("choose_option")}
                                                onChange={handleServicePointChange}
                                                selected={selectedServicePointIndex}
                                                closeOnSelection
                                                disabled={!hasOrderManagePermission || !canEdit}
                                                searchOptions={{
                                                    enabled: true,
                                                    placeholder: t("search")
                                                }}
                                                styles={{
                                                    minWidth:300
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>


                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                flexDirection:'column',
                                gap:1.5,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:1,
                                    alignItems:"flex-start",
                                    marginTop:4
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140
                                    }}
                                >
                                    <Text light>{t("skip_address_validation")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <ShSwitch
                                        checked={orderData.shippingAddress?.skipValidation ?? false}
                                        onChange={(e) => handleChange("skipValidation", e.target.checked)}
                                        disabled={!hasOrderManagePermission || !canEdit}
                                    />
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    flex:1,
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:80
                                        }
                                    }}
                                >
                                    <Text light>{t("remark")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        flex:1,
                                        height:132,
                                        overflow:"hidden",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <ShValidatedArea
                                        sx={{
                                            masterBox:{
                                            },
                                            inputBox:{
                                                // display:"flex",
                                                // alignItems:"flex-start",
                                            },
                                            input: {
                                                lineHeight:"unset",
                                                width:1,
                                            }
                                        }}
                                        disabled={!hasOrderManagePermission}
                                        name="remark"
                                        value={orderData.remark ?? ""}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Maps */}
                {/* <Box 
                    sx={{
                        width:200,
                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]
                    }}
                ></Box> */}
            </Box>
            <SpDesktopFooter>
                {/* Pass the necessary state values and setter functions to GridActions */}
                <GridActions
                    isLoading={isLoading}
                   onclick={handleSaveClick}
                   canEdit={canEdit}
                   hasPermission={hasOrderManagePermission}
                />
            </SpDesktopFooter>
        </Box>
    );
}


export default ShipOrder;